import { useEffect, useState } from "react";
import type { RefObject } from "react";

/** Alvo observável: um elemento do DOM ou um objeto que o expõe (ex.: `Avatar` do PrimeReact). */
export type InViewTarget = Element | { getElement(): Element | null | undefined };

/** Sem IntersectionObserver (ex.: jsdom, navegadores antigos) o elemento conta como visível. */
function supportsObserver(): boolean {
  return typeof IntersectionObserver !== "undefined";
}

function resolveElement(target: InViewTarget | null): Element | null | undefined {
  if (!target) return null;

  return "getElement" in target ? target.getElement() : target;
}

/**
 * `true` assim que o alvo entra na viewport — e continua `true` depois (sair e voltar
 * da tela não dispara nova busca). O alvo é lido do `ref` ao montar; sem alvo, conta como visível.
 */
export function useInView(ref: RefObject<InViewTarget | null>): boolean {
  const [inView, setInView] = useState(() => !supportsObserver());

  useEffect(() => {
    if (inView) return;

    const element = resolveElement(ref.current);
    if (!element) {
      setInView(true);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setInView(true);
        observer.disconnect();
      }
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [inView, ref]);

  return inView;
}
